/**
 * CevaWarrantyPanel — Jul-2026 (User-Wunsch).
 * CEVA-Lieferung = Garantieteile: gescannte Positionen per 1 Klick einem
 * offenen Garantie-Auftrag zubuchen (Positionen mit Abrechnungsart Garantie
 * + Bestands-Abgang).
 * Aug-2026 (User-Wunsch Jörg): Wird KEIN offener Garantie-Auftrag gefunden,
 * werden die Teile automatisch als „nicht zugeordnete Garantieteile" abgelegt
 * und erscheinen im Tab „Offene Garantien" der Werkstatt zur späteren Zuordnung.
 */
import { useState, useEffect, useRef } from "react";
import { Loader2, ShieldCheck, Wrench } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export const CevaWarrantyPanel = ({ scan }) => {
    const orders = scan.warranty?.orders || [];
    const [orderId, setOrderId] = useState(orders[0]?.work_order_id || "");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [parked, setParked] = useState(null);
    const parkedRef = useRef(false);

    // scan.items sind „matched"-Wrapper ({scanned, match, …}) — rohe Items tolerieren.
    const toItems = () => (scan.items || []).map((m) => {
        const it = m.scanned || m;
        return {
            name: it.name, sku: it.sku, quantity: it.quantity || 1,
            purchase_price_net: it.unit_price_net || it.purchase_price_net || m.match?.purchase_price_net || 0,
        };
    }).filter((it) => it.name);

    // Kein Auftrag gefunden → Teile automatisch als „nicht zugeordnet" parken.
    useEffect(() => {
        if (orders.length > 0 || parkedRef.current) return;
        const items = toItems();
        if (!items.length) return;
        parkedRef.current = true;
        api.post("/wms/warranty/unassigned", {
            items,
            source: scan.delivery_note_number || scan.supplier_name || null,
        }).then((r) => setParked(r.data)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const assign = async () => {
        if (!orderId) { toast.error("Bitte Garantie-Auftrag wählen"); return; }
        setBusy(true);
        try {
            const r = await api.post("/wms/warranty/assign-parts", { work_order_id: orderId, items: toItems() });
            setDone(r.data);
            toast.success(`${r.data.added} Garantieteil(e) auf Auftrag ${r.data.order_number} gebucht (${r.data.stock_booked} mit Bestands-Abgang)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zubuchen fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-1.5 border border-emerald-300 bg-emerald-50 rounded-sm px-2.5 py-2" data-testid="scan-ceva-panel">
            <p className="text-[11px] font-bold text-emerald-900 inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> CEVA Logistics erkannt — Garantieteile. Bestand wird normal gebucht; Teile zusätzlich einem Garantie-Auftrag zubuchen:
            </p>
            {done ? (
                <p className="text-[11px] font-bold text-emerald-800 mt-1" data-testid="scan-ceva-done">
                    ✓ {done.added} Position(en) auf Auftrag {done.order_number} gebucht.
                </p>
            ) : orders.length === 0 ? (
                <p className="text-[11px] text-emerald-800 mt-1" data-testid="scan-ceva-parked">
                    Kein offener Garantie-Auftrag gefunden — die Teile wurden als
                    <b> nicht zugeordnete Garantieteile</b> abgelegt und erscheinen in der
                    Werkstatt unter <b>Offene Garantien</b> zur 1-Klick-Zuordnung
                    {parked ? ` (${parked.created} Teil(e) abgelegt).` : "."}
                </p>
            ) : (
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <select value={orderId} onChange={(e) => setOrderId(e.target.value)}
                        className="h-8 px-2 rounded-sm border border-emerald-300 text-xs bg-white"
                        data-testid="scan-ceva-order-select">
                        {orders.map((o) => (
                            <option key={o.work_order_id} value={o.work_order_id}>
                                {o.order_number} · {o.customer_name}{o.vehicle ? ` · ${o.vehicle}` : ""}
                            </option>
                        ))}
                    </select>
                    <button type="button" onClick={assign} disabled={busy}
                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white bg-emerald-700 hover:bg-emerald-800 rounded-sm px-3 py-2 disabled:opacity-60"
                        data-testid="scan-ceva-assign-btn">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />} Teile dem Garantieauftrag zubuchen
                    </button>
                </div>
            )}
        </div>
    );
};
